import React, { useState, useRef, useMemo, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Badge,
    Popover,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    ToggleButton,
    ToggleButtonGroup,
    Divider,
    Chip,
    Collapse,
    TextField,
    Paper,
    Grid,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector, useDispatch } from "react-redux";
import TopSection from "../LT/TopSection";
import CustomButton from "../../components/common/button/Button";
import { Loader } from "../../components/common/Loader";
import { MultiDropdownSelection } from "../../components/common/MultiDropdownSelection";
import DropdownPicker from "../../components/common/Dropdown";
import DatePickerInput from "../../components/common/DatePicker";
import dayjs from "dayjs";
import { toast } from 'react-toastify';
import Cards from "../../components/common/card/Card";
import { getPOExceptionMgmtBuyerTable } from "../../redux/actions/getPOExceptionMgmtBuyerTable";
import { getPOExceptionMgmtBuyerFilter } from "../../redux/actions/getPOExceptionMgmtBuyerFilter";
import { getPOExceptionMgmtBuyerTableEndpoint, getMaterialBatchDataEndpoint, getSupplierEmailsEndpoint, addToSupplierEmailsEndpoint, sendMailToSupplierEndpoint, sendToSupplierEndpoint, updateCollabStatusEndpoint, updateExceptionStatusEndpoint, getPOExceptionMgmtBuyerTablefilterEndpoint } from "../../common/endpoints";
import { getMaterialBatchData } from "../../redux/actions/getMaterialBatchDataSlice";
import { getSupplierEmails } from "../../redux/actions/getSupplierEmailsSlice";
import { addToSupplierEmails } from "../../redux/actions/addToSupplierEmailsSlice";
import { sendMailToSupplier } from "../../redux/actions/sendMailToSupplierSlice";
import { sendToSupplier } from "../../redux/actions/sendToSupplierSlice";
import { updateCollabStatus } from "../../redux/actions/updateCollabStatusSlice";
import { updateExceptionStatus } from "../../redux/actions/updateExceptionStatusSlice";
import { selectDropdownOptionsData, selectDropdownOptionsLoading } from "../../redux/actions/getPOExceptionMgmtBuyerFilter";
import BatchTable from "./BatchTable";
import { VariantViewModal } from "../../components/common/VariantView/VariantViewModal";
import { ShowVariantViewModal } from "../../components/common/VariantView/ShowVariantViewModal";
import "./POExceptionMgmtBuyer.css"

const QtyEditCell = ({ params, onCommit, disabled }) => {
    const [localValue, setLocalValue] = useState(params.data?.NEW_PO_QUANTITY ?? '');
    return (
        <TextField
            value={localValue}
            size="small"
            disabled={disabled}
            inputProps={{ style: { fontSize: '13px', padding: '4px 8px' } }}
            onChange={(e) => {
                const newVal = e.target.value;
                setLocalValue(newVal);
                onCommit(params, newVal);
            }}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
        />
    );
};

const RescheduleDateCell = ({ params, onCommit, disabled, field = 'RESCHEDULING_DATE' }) => {
    const raw = params.data?.[field];
    const parsed = raw && raw !== '-' ? dayjs(raw) : null;
    const [localValue, setLocalValue] = useState(parsed);
    return (
        <DatePickerInput
            value={localValue}
            size="small"
            height={32}
            fontSize="13px"
            format="DD/MM/YYYY"
            disabled={disabled}
            onChange={(newVal) => {
                setLocalValue(newVal);
                const formatted = newVal && newVal.isValid() ? newVal.format('YYYY-MM-DD') : '';
                onCommit(params, formatted);
            }}
        />
    );
};

const EXCEPTION_OPTIONS = [
    { label: 'Not Sent', value: 'actionNeeded', color: '#9A3412' },
    { label: 'Awaiting for Response', value: 'notified', color: '#854D0E' },
    { label: 'Response Received', value: 'supplierResponses',  color: '#166534' },
];

const DATE_FILTER_PARAMS = {
    filters: [
        {
            filter: 'agTextColumnFilter',
            filterParams: { suppressMiniFilter: false },
        },
        {
            filter: 'agSetColumnFilter',
            filterParams: {
                excelMode: 'windows',
                suppressMiniFilter: false,
                buttons: ['apply', 'reset'],
                valueFormatter: (params) => {
                    const v = params.value;
                    if (!v || v === '-') return '-';
                    const d = dayjs(v);
                    return d.isValid() ? d.format('DD/MM/YYYY') : v;
                },
            },
        },
    ],
};

const getSapPurchaseOrderUrl = (poNo) => {
    const baseUrl = process.env.REACT_APP_SAP_PO_EDIT_BASE_URL;   
    return `${baseUrl}#PurchaseOrder-display?PurchaseOrder=${poNo}&sap-ui-tech-hint=GUI&uitype=advanced`;
};

const POExceptionMgmtBuyer = () => {

    const getInitialFormData = () => ({
        plant: [],
        material: [],
        mrpController: [],
        supplier: [],
        poNumber: [],
        // exception: [],
    });

    const formKeyToApiKey = {
        material: 'materialIds',
        plant: 'plantIds',
        mrpController: 'mrpControllers',
        supplier: 'vendorIds',
        poNumber: 'poNos',
        // exception: 'exceptionIds',
    };

    const gridRef = useRef();
    const restoredGridFiltersRef = useRef(false);
    const filterDebounceRef = useRef(null);
    const formDataRef = useRef(getInitialFormData());
    const dispatch = useDispatch();
    const [batchModalOpen, setBatchModalOpen] = useState(false);
    const [supplierCommModalOpen, setSupplierCommModalOpen] = useState(false);
    const poExceptionMgmtBuyerTableSelector = useSelector((state) => state.getPOExceptionMgmtBuyerTable);
    const poExceptionMgmtBuyerFilterSelector = useSelector((state) => state.getPOExceptionMgmtBuyerFilter);
    const materialBatchDataSelector = useSelector((state) => state.getMaterialBatchData);
    const supplierEmailsSelector = useSelector((state) => state.getSupplierEmails);
    const selectedRegion = useSelector((state) => state.setRegionPlantStore.selectedRegion);
    const plantNameOnly = useSelector((state) => state.setRegionPlantStore.selectedPlant);
    const selectedPlant = plantNameOnly
        ? plantNameOnly.split(" - ")[0]
        : null;
    const userRole = localStorage.getItem('userRole');

    const dropdownData = useSelector(selectDropdownOptionsData);
    const dropdownLoading = useSelector(selectDropdownOptionsLoading);


    const [filterCondition, setFilterCondition] = useState('withExceptions');
    const [availableFilters, setAvailableFilters] = useState({});
    const [trackingData, setTrackingData] = useState([]);
    const [selectedMaterialBatches, setSelectedMaterialBatches] = useState(null);
    const [supplierCommData, setSupplierCommData] = useState(null);
    const [disableFilterBtn, setDisableFilterBtn] = useState(true);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [savedGridFilterModel, setSavedGridFilterModel] = useState(null);
    const [formData, setFormData] = useState(getInitialFormData);
    const [gridApi, setGridApi] = useState();
    const [sendToSupplierModalOpen, setSendToSupplierModalOpen] = useState(false);
    const [selectedRecipients, setSelectedRecipients] = useState([]);
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [addEmailFormOpen, setAddEmailFormOpen] = useState(false);
    const [newEmailData, setNewEmailData] = useState({ contactName: '', emailAddress: '' });
    const [exceptionsAnchorEl, setExceptionsAnchorEl] = useState(null);
    const [isUnconfirmed, setIsUnconfirmed] = useState(false);
    const [isAriba, setIsAriba] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [openCreateVariantModal, setOpenCreateVariantModal] = useState(false);
    const [openShowVariantModal, setOpenShowVariantModal] = useState(false);
    const [variantData, setVariantData] = useState({});
    const isViewerRole = ['Global Viewer', 'Regional Viewer'].includes(localStorage.getItem('userRole'));

    useEffect(() => {
        if (!dropdownData) return;

        setFormData((prev) => {
            let changed = false;
            const updated = { ...prev };

            Object.entries(formKeyToApiKey).forEach(([field, optionsKey]) => {
                const currentSelected = prev[field];
                if (!Array.isArray(currentSelected) || currentSelected.length === 0) return;

                const availableOptions = dropdownData[optionsKey];
                if (!Array.isArray(availableOptions)) return;

                const filtered = currentSelected.filter((sel) =>
                    availableOptions.some((opt) => opt.value === sel.value)
                );

                if (filtered.length !== currentSelected.length) {
                    updated[field] = filtered;
                    changed = true;
                }
            });

            return changed ? updated : prev;
        });
    }, [dropdownData]);

    useEffect(() => {
        const isAll = !selectedPlant || selectedPlant.toUpperCase() === 'ALL';
        const newPlant = isAll ? [] : [{ title: selectedPlant, value: selectedPlant }];
        const freshFormData = { ...getInitialFormData(), plant: newPlant };
        setFormData(freshFormData);
        formDataRef.current = freshFormData;
        const filterData = isAll ? getInitialFormData() : { ...getInitialFormData(), plant: [selectedPlant] };
        if (userRole && selectedRegion) {
            fetchDropdownOptions(filterData);
            fetchTable({ filters: freshFormData });
        }
    }, [dispatch, userRole, selectedRegion, selectedPlant]);
    

    const getOptions = (key) => {
        try {
            return (dropdownData && dropdownData[key]) ? dropdownData[key] : [];
        } catch (e) {
            return [];
        }
    };

    const handleAddEmailToggle = () => {
        setAddEmailFormOpen((prev) => !prev);
        setNewEmailData({ contactName: '', emailAddress: '' });
    };

    const handleAddEmailCancel = () => {
        setAddEmailFormOpen(false);
        setNewEmailData({ contactName: '', emailAddress: '' });
    };

    const handleAddEmailSave = async () => {
        const selectedRow = selectedRows[0] || {};
        const requestBody = {
            plantId: selectedRow.PLANT_ID || '',
            supplierId: selectedRow.VENDOR_ID || '',
            userEmail: newEmailData.emailAddress,
            // parentVendorName: newEmailData.contactName,
            supplierName: selectedRow.VENDOR_NAME || '',
            userName: newEmailData.contactName,
        };
        try {
            const response = await dispatch(addToSupplierEmails({
                dynamicEndpoint: addToSupplierEmailsEndpoint,
                data: requestBody,
            }));
            if (response?.meta?.requestStatus === 'fulfilled') {
                setAddEmailFormOpen(false);
                setNewEmailData({ contactName: '', emailAddress: '' });
                toast.success('Email address added', { autoClose: 1500 });
                const plantIds = [...new Set(selectedRows.map((r) => r.PLANT_ID).filter(Boolean))].join(',');
                const supplierIds = [...new Set(selectedRows.map((r) => r.VENDOR_ID).filter(Boolean))].join(',');
                dispatch(getSupplierEmails({
                    dynamicEndpoint: getSupplierEmailsEndpoint,
                    params: { plantIds, supplierIds },
                }));
            } else {
                toast.error('Failed to add email address. Please try again.', { autoClose: 2000 });
            }
        } catch (e) {
            console.error('Failed to add supplier email', e);
            toast.error('Failed to add email address. Please try again.', { autoClose: 2000 });
        }
    };

    const recipients = useMemo(
        () =>
            (supplierEmailsSelector?.data || []).map((item) => ({
                id: item.EMAIL_SEQ_ID,
                name: item.USER_NAME || "",
                role: (item.SUPPLIER_TYPE || '').toLowerCase(),
                email: item.USER_EMAIL,
                vendorCode: item.SUPPLIER_ID,
                company: item.SUPPLIER_NAME,
            })),
        [supplierEmailsSelector?.data]
    );

    const handleRecipientToggle = (id) => {
        setSelectedRecipients((prev) =>
            prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
        );
    };

    const handleCloseSendModal = () => {
        setSendToSupplierModalOpen(false);
        setSelectedRecipients([]);
    };

    const handleSendNotification = async () => {
        // const mrpControllerName = selectedRows[0]?.MRP_CONTROLLER_NAME || '';
        const selectedRecipientData = recipients.filter((r) => selectedRecipients.includes(r.id));
        const poNos = selectedRows.map((r) => r.PO_NO).filter(Boolean);

        const mailRequestBody = selectedRecipientData.map((r) => ({
            // mrpControllerName,
            userName: r.name || "",
            role: r.role,
            email: r.email,
            supplierId: r.vendorCode,
            supplierName: r.company,
            plantId: selectedRows[0]?.PLANT_ID || '',
            poNos,
        }));

        const currentSelectedRows = selectedRows.map(
            (row) => trackingData.find((r) => r.MGMT_ID === row.MGMT_ID) || row
        );

        const sendToSupplierRequestBody = {
            sendToSupplier: currentSelectedRows.map((row) => ({
                plantId: row.PLANT_ID || '',
                materialId: row.MATERIAL_ID || '',
                poNo: row.PO_NO || '',
                newQty: row.NEW_PO_QUANTITY ?? null,
                newShippingInstructions: row.NEW_PO_SHIPPING_INSTRUCTION || '',
                mgmtId: row.MGMT_ID || '',
                rescheduleExceptionNumber: row.RESCHEDULE_EXCEPTION_NUMBER || null,
                newDeliveryDate: row.NEW_DELIVERY_DATE || '',
                newHandoverDate: row.NEW_HANDOVER_DATE || ''
            })),
        };

        try {
            const [mailResponse, sendResponse] = await Promise.all([
                dispatch(sendMailToSupplier({
                    dynamicEndpoint: sendMailToSupplierEndpoint,
                    data: mailRequestBody,
                })),
                dispatch(sendToSupplier({
                    dynamicEndpoint: sendToSupplierEndpoint,
                    data: sendToSupplierRequestBody,
                })),
            ]);

            const bothSucceeded =
                mailResponse?.meta?.requestStatus === 'fulfilled' &&
                sendResponse?.meta?.requestStatus === 'fulfilled';

            if (bothSucceeded) {
                handleCloseSendModal();
                toast.success('Notification sent to supplier successfully', { autoClose: 2000 });
            } else {
                toast.error('Failed to send notification. Please try again.', { autoClose: 2000 });
            }
        } catch (e) {
            console.error('Failed to send notification to supplier', e);
            toast.error('Failed to send notification. Please try again.', { autoClose: 2000 });
        }
    };

    const handleViewBatches = async (materialId, materialName, plantId) => {
        setSelectedMaterialBatches({ material: materialName, materialId, plantId });
        setBatchModalOpen(true);
        try {
            await dispatch(getMaterialBatchData({
                dynamicEndpoint: getMaterialBatchDataEndpoint,
                params: { materialId, plantId },
            }));
        } catch (e) {
            console.error('Failed to fetch batch data', e);
        }
    };

    const handleBatchDialogClose = () => {
        setBatchModalOpen(false);
        setSelectedMaterialBatches(null);
    };

    const handleViewSupplierComm = (comment, poNumber) => {
        setSupplierCommData({ comm: comment, po: poNumber });
        setSupplierCommModalOpen(true);
    };

    const extractValues = (arr) => (arr || []).map((item) => (item && typeof item === 'object' ? item.value : item));

    const fetchTable = async ({ filterCondition: fc, filters, unconfirmed: unc, ariba: arb } = {}) => {
        try {
            const fd = filters ?? formData;
            const plants = extractValues(fd.plant);
            const materials = extractValues(fd.material);
            const suppliers = extractValues(fd.supplier);
            const poNumbers = extractValues(fd.poNumber);
            const mrpControllers = extractValues(fd.mrpController);
            const exceptions = extractValues(fd.exception);
            const requestBody = {
                filterCondition: exceptions.length > 0 ? exceptions[0] : fc ?? filterCondition,
                plantIds: plants.some(p => String(p).toUpperCase() === 'ALL') ? [] : (plants.length === 0 && selectedPlant && selectedPlant.toUpperCase() !== 'ALL' ? [selectedPlant] : plants),
                materialIds: materials,
                mrpControllerIds: mrpControllers,
                supplierIds: suppliers,
                poNos: poNumbers,
                abUnconfirmed: (unc !== undefined ? unc : isUnconfirmed) ? 'yes' : '',
                ariba: (arb !== undefined ? arb : isAriba) ? 'yes' : '',
            };
            const isGlobalRole = userRole === 'Global Planner' || userRole === 'Global Viewer';
            const response = await dispatch(getPOExceptionMgmtBuyerTable({
                dynamicEndpoint: getPOExceptionMgmtBuyerTableEndpoint,
                params: {
                    role: userRole,
                    ...(isGlobalRole ? {} : { region: selectedRegion}),
                },
                data: requestBody,
            }));
            if (response?.meta?.requestStatus === 'fulfilled') {
                const payload = response.payload;
                setTrackingData(payload?.data ?? []);
                setAvailableFilters(payload?.availableFilters ?? {});
            }
        } catch (e) {
            console.error('Failed to fetch PO exception table', e);
        }
    };

    useEffect(() => {
        fetchTable({ filterCondition });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterCondition, selectedRegion, userRole]);

    const GRID_FILTER_MODEL_STORAGE_KEY = 'poEmailManagement.gridFilterModel';
    const FORM_DATA_STORAGE_KEY = 'poEmailManagement.formData';
    const HIDDEN_COLUMNS_STORAGE_KEY = 'poEmailManagement.hiddenColumns';

    const poEmailManagementColumnDefs = [
        {
            field: 'PLANT_ID',
            headerName: 'PLANT CODE',
            width: 190,
            pinned: 'left',
            lockPinned: true,
            lockPosition: true,
            valueGetter: (params) => {
                if (!params.data) return '-';
                const id = params.data.PLANT_ID;
                const name = params.data.PLANT_NAME;
                const parts = [id, name].filter((v) => v !== null && v !== undefined && v !== '');
                return parts.length > 0 ? parts.join(' | ') : '-';
            },
        },
        {
            field: 'MATERIAL_ID',
            headerName: 'MATERIAL NUMBER',
            width: 130,
            pinned: 'left',
            lockPinned: true,
            lockPosition: true,
            cellStyle: { color: '#b91c1c', fontWeight: 'bold' },
        },
        {
            field: 'MATERIAL_NAME',
            headerName: 'MATERIAL DESCRIPTION',
            width: 180,
            pinned: 'left',
            lockPinned: true,
            lockPosition: true,
        },
        {
            field: 'PARENT_VENDOR_ID',
            headerName: 'PARENT VENDOR',
            width: 200,
            // pinned: 'left',
            // lockPinned: true,
            // lockPosition: true,
            valueGetter: (params) => {
                if (!params.data) return '-';
                const id = params.data.PARENT_VENDOR_ID;
                const name = params.data.PARENT_VENDOR_NAME;
                const parts = [id, name].filter((v) => v !== null && v !== undefined && v !== '');
                return parts.length > 0 ? parts.join(' | ') : '-';
            },
        },
        {
            field: 'VENDOR_ID',
            headerName: 'VENDOR',
            width: 200,
            // pinned: 'left',
            // lockPinned: true,
            // lockPosition: true,
            valueGetter: (params) => {
                if (!params.data) return '-';
                const id = params.data.VENDOR_ID;
                const name = params.data.VENDOR_NAME;
                const parts = [id, name].filter((v) => v !== null && v !== undefined && v !== '');
                return parts.length > 0 ? parts.join(' | ') : '-';
            },
        },
        {
            field: 'PURCHASING_GROUP',
            headerName: 'PURCHASING GROUP',
            width: 180,
        },
        {
            field: 'MRP_CONTROLLER',
            headerName: 'MRP CONTROLLER',
            width: 180,
        },
        {
            field: 'REGION',
            headerName: 'REGION',
            width: 120,
        },
        {
            field: 'CONFIRMATION_CONTROL_KEY',
            headerName: 'CONFIRMATION CONTROL',
            width: 170,
        },
        {
            field: 'PO_CREATION_TYPE',
            headerName: 'PO CREATION TYPE',
            width: 160,
        },
        {
            field: 'PO_NO',
            headerName: 'PO #',
            width: 150,
            headerClass: 'bg-gray-100',
        },
        {
            field: 'PO_ITEM_NO',
            headerName: 'PO ITEM NO',
            width: 120,
            headerClass: 'bg-gray-100',
        },
        {
            field: 'CREATION_DATE',
            headerName: 'CREATION DATE',
            width: 170,
            headerClass: 'bg-gray-100',
            filterParams: DATE_FILTER_PARAMS,
            valueFormatter: (params) => { const v = params.value; if (!v || v === '-') return '-'; const d = dayjs(v); return d.isValid() ? d.format('DD/MM/YYYY') : v; },
        },
        {
            field: 'HANDOVER_DATE',
            headerName: 'HANDOVER DATE',
            width: 180,
            headerClass: 'bg-gray-100',
            filterParams: DATE_FILTER_PARAMS,
            valueFormatter: (params) => { const v = params.value; if (!v || v === '-') return '-'; const d = dayjs(v); return d.isValid() ? d.format('DD/MM/YYYY') : v; },
        },
        {
            field: 'DELIVERY_DATE',
            headerName: 'DELIVERY DATE',
            width: 240,
            cellStyle: { fontWeight: 'bold' },
            headerClass: 'bg-yellow-50',
            filterParams: DATE_FILTER_PARAMS,
            valueFormatter: (params) => { const v = params.value; if (!v || v === '-') return '-'; const d = dayjs(v); return d.isValid() ? d.format('DD/MM/YYYY') : v; },
        },
        {
            field: 'AB_CONFIRMATION_DATE',
            headerName: 'AB CONFIRMATION',
            width: 200,
            filterParams: DATE_FILTER_PARAMS,
            valueFormatter: (params) => { const v = params.value; if (!v || v === '-') return '-'; const d = dayjs(v); return d.isValid() ? d.format('DD/MM/YYYY') : v; },
        },
        {
            field: 'OUTLINE_AGREEMENT',
            headerName: 'OUTLINE AGREEMENT',
            width: 160,
        },
        {
            field: 'CONTRACT_ITEM',
            headerName: 'CONTRACT ITEM',
            width: 160,
        },
        {
            field: 'ARIBA',
            headerName: 'ARIBA',
            width: 120,
            cellStyle: { textAlign: 'center' },
        },
        {
            field: 'INCOTERMS',
            headerName: 'INCOTERM',
            width: 140,
        },
        {
            field: 'INCOTERMS_LOCATION1',
            headerName: 'INCOTERM LOCATION 1',
            width: 150,
        },
        {
            field: 'INCOTERMS_LOCATION2',
            headerName: 'INCOTERM LOCATION 2',
            width: 150,
        },
        {
            field: 'EXCEPTION_MESSAGE',
            headerName: 'EXCEPTION MESSAGE',
            width: 190,
            cellStyle: { textAlign: 'center' },
            headerClass: 'bg-gray-100',
            valueGetter: (params) => {
                if (!params.data) return '-';
                const msg = params.data.RESCHEDULE_EXCEPTION_NUMBER;
                const name = params.data.EXCEPTION_MESSAGE;
                const parts = [msg, name].filter((v) => v !== null && v !== undefined && v !== '');
                return parts.length > 0 ? parts.join(' | ') : '-';
            },
        },
         {
            field: 'RESCHEDULED_GR_DATE',
            headerName: 'RES.DATE GR',
            width: 180,
            headerClass: 'bg-gray-100',
            filterParams: DATE_FILTER_PARAMS,
            valueFormatter: (params) => { const v = params.value; if (!v || v === '-') return '-'; const d = dayjs(v); return d.isValid() ? d.format('DD/MM/YYYY') : v; },
        },
        {
            field: 'RESCHEDULING_DATE',
            headerName: 'RESCH.DATE',
            width: 190,
            headerClass: 'bg-gray-100',
            filterParams: DATE_FILTER_PARAMS,
            valueFormatter: (params) => { const v = params.value; if (!v || v === '-') return '-'; const d = dayjs(v); return d.isValid() ? d.format('DD/MM/YYYY') : v; },
        },
         {
            field: 'NEW_DELIVERY_DATE',
            headerName: 'DELIVERY DATE (NEW)',
            width: 190,
            headerClass: 'bg-gray-100',
        },
        {
            field: 'NEW_HANDOVER_DATE',
            headerName: 'HANDOVER DATE (NEW)',
            width: 190,
            headerClass: 'bg-gray-100',
        },
        {
            field: 'PO_QUANTITY',
            headerName: 'PO QTY',
            width: 160,
        },
        {
            field: 'NEW_PO_QUANTITY',
            headerName: 'PO QTY (NEW)',
            width: 160,
        },
        {
            field: 'UOM',
            headerName: 'UOM',
            width: 110,
        },
        {
            field: 'PO_SHIPPING_INSTRUCTION',
            headerName: 'PO SHIPPING INSTRUCTIONS',
            width: 220,
        },
        {
            field: 'NEW_PO_SHIPPING_INSTRUCTION',
            headerName: 'PO SHIPPING INSTRUCTIONS (NEW)',
            width: 220,
        },
        {
            field: 'RECORD_STATUS',
            headerName: 'SUPPLIER DECISION',
            width: 220,
            headerClass: 'bg-green-50',
        },
        {
            field: 'EXCEPTION_STATUS',
            headerName: 'EXCEPTION STATUS',
            width: 200,
            headerClass: 'bg-green-50',
        },
        {
            field: 'USER_COMMENTS',
            headerName: 'SUPPLIER COMMENT',
            width: 200,
            headerClass: 'bg-green-50',
        },
        {
            field: 'SUPPLIER_CONFIRMED_DATE',
            headerName: 'SUPPLIER CONFIRMED DELIVERY DATE',
            width: 240,
            headerClass: 'bg-green-50',
            filterParams: DATE_FILTER_PARAMS,
            valueFormatter: (params) => { const v = params.value; if (!v || v === '-') return '-'; const d = dayjs(v); return d.isValid() ? d.format('DD/MM/YYYY') : v; },
        },
        {
            field: 'SUPPLIER_CONFIRMED_HANDOVER_DATE',
            headerName: 'SUPPLIER CONFIRMED HANDOVER DATE',
            width: 240,
            headerClass: 'bg-green-50',
            filterParams: DATE_FILTER_PARAMS,
            valueFormatter: (params) => { const v = params.value; if (!v || v === '-') return '-'; const d = dayjs(v); return d.isValid() ? d.format('DD/MM/YYYY') : v; },
        },
        {
            field: 'COLLABORATION_STATUS',
            headerName: 'STATUS',
            width: 200,
        },
        {
            field: 'MD04_URL',
            headerName: 'MD04 URL',
            width: 130,
            sortable: false,
            filter: false,
            cellRenderer: (params) => {
                const material = params.data?.MATERIAL_ID ?? '';
                const plant = params.data?.PLANT_ID ?? '';
                const url = `${process.env.REACT_APP_SAP_PO_BASE_URL}#MRPMaterial-monitorSupplyAndDemand?Material=${material}&MRPPlant=${plant}`;
                return (
                    <IconButton
                        size="small"
                        onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                        sx={{ color: '#000000' }}
                    >
                        <OpenInNewIcon fontSize="small" />
                    </IconButton>
                );
            },
        },
        {
            field: 'COMMAND_CENTER_URL',
            headerName: 'COMMAND CENTER URL',
            width: 130,
            sortable: false,
            filter: false,
            cellRenderer: (params) => {
                const poNumber = params.data?.PO_NO ?? '';
                const plant = params.data?.PLANT_ID ?? '';
                const url = `https://alcommandcenter.ko.com/core/supply/order/order-detail/standardPO/${poNumber}?plant=${plant}`;
                return (
                    <IconButton
                        size="small"
                        onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                        sx={{ color: '#000000' }}
                    >
                        <OpenInNewIcon fontSize="small" />
                    </IconButton>
                );
            },
        },
        {
            field: 'ACTIONS',
            headerName: 'ACTIONS',
            width: 120,
            sortable: false,
            filter: false,
            pinned: 'right',
        },
    ];

    const isEmptyValue = (value) => value === null || value === undefined || value === '';
    const getValueOrDash = (value) => (isEmptyValue(value) ? '-' : value);
    const formatDateValue = (value) => {
        if (isEmptyValue(value) || value === '-') return '-';
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed.format('DD/MM/YYYY') : value;
    };

    const getSupplierDecisionColor = (status) => {
        switch ((status || '').toUpperCase()) {
            case 'AWAITING FOR RESPONSE':
                return { textColor: '#854D0E', bgColor: '#FEF9C3' };
            case 'REJECTED':
                return { textColor: '#991B1B', bgColor: '#FEE2E2' };
            case 'APPROVED':
                return { textColor: '#166534', bgColor: '#DCFCE7' };
            case 'NOT SENT':
                return { textColor: '#9A3412', bgColor: '#FFEDD5' };
            case 'COUNTER OFFER':
                return { textColor: '#492901', bgColor: '#fadead' };
            default:
                return { textColor: 'inherit', bgColor: 'transparent' };
        }
    };

    const handleDropdownChange = (params, newValue) => {
        const mgmtId = params.data?.MGMT_ID;
        setTrackingData((prevRows) =>
            prevRows.map((row) =>
                row.MGMT_ID === mgmtId
                    ? { ...row, NEW_PO_SHIPPING_INSTRUCTION: newValue }
                    : row
            )
        );
    }

    const handleCollaborationStatusChange = async (params, newValue) => {
        const mgmtId = params.data?.MGMT_ID;
        setTrackingData((prevRows) =>
            prevRows.map((row) =>
                row.MGMT_ID === mgmtId
                    ? { ...row, COLLABORATION_STATUS: newValue }
                    : row
            )
        );
        try {
            const requestBody = {
                plantId: params.data?.PLANT_ID || '',
                supplierId: params.data?.VENDOR_ID || '',
                poNo: params.data?.PO_NO || '',
                collabStatus: newValue,
            };
            const response = await dispatch(updateCollabStatus({
                dynamicEndpoint: updateCollabStatusEndpoint,
                data: requestBody,
            }));
            if (response?.meta?.requestStatus === 'fulfilled') {
                toast.success('Collaboration status updated successfully.', { autoClose: 2000 });
                fetchTable({ filterCondition, filters: formData });
            } else {
                toast.error('Failed to update collaboration status. Please try again.', { autoClose: 2000 });
                setTrackingData((prevRows) =>
                    prevRows.map((row) =>
                        row.MGMT_ID === mgmtId
                            ? { ...row, COLLABORATION_STATUS: params.data?.COLLABORATION_STATUS ?? '' }
                            : row
                    )
                );
            }
        } catch (e) {
            console.error('Failed to update collaboration status', e);
            toast.error('Failed to update collaboration status. Please try again.', { autoClose: 2000 });
        }
    };

    const handleTextInputChange = (params, newValue) => {
        const mgmtId = params.data?.MGMT_ID;
        setTrackingData((prevRows) =>
            prevRows.map((row) =>
                row.MGMT_ID === mgmtId ? { ...row, NEW_PO_QUANTITY: newValue } : row
            )
        );
    };

    const handleDateInputChange = (field) => (params, newValue) => {
        const mgmtId = params.data?.MGMT_ID;
        setTrackingData((prevRows) =>
            prevRows.map((row) =>
                row.MGMT_ID === mgmtId ? { ...row, [field]: newValue } : row
            )
        );
    };

    const handleCloseException = async (params) => {
        const row = params.data || {};
        const requestBody = {
            mgmtIds: [row.MGMT_ID].filter(Boolean),
            plantIds: [row.PLANT_ID || ''],
            vendorIds: [row.VENDOR_ID || ''],
            materialIds: [row.MATERIAL_ID || ''],
            poNos: [row.PO_NO || ''],
            exceptionStatus: 'Closed',
        };
        try {
            const response = await dispatch(updateExceptionStatus({
                dynamicEndpoint: updateExceptionStatusEndpoint,
                data: requestBody,
            }));
            if (response?.meta?.requestStatus === 'fulfilled') {
                toast.success('Exception closed successfully.', { autoClose: 2000 });
                fetchTable({ filterCondition, filters: formData });
            } else {
                toast.error('Failed to close. Please try again.', { autoClose: 2000 });
            }
        } catch (e) {
            console.error('Failed to close', e);
            toast.error('Failed to close. Please try again.', { autoClose: 2000 });
        }
    };



    const columnDefsWithHandlers = useMemo(() => poEmailManagementColumnDefs.map((col, index) => {
        const colKey = col.field || col.headerName;
        const isHidden = selectedColumns.includes(colKey);

        if (col.field === 'MATERIAL_ID') {
            return {
                ...col,
                hide: isHidden,
                cellRenderer: (params) => (
                    <Typography
                        sx={{
                            cursor: 'pointer',
                            color: '#DC2626',
                            fontWeight: 500,
                            fontSize: '14px',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                        onClick={() => handleViewBatches(params.value, params.data?.MATERIAL_NAME, params.data?.PLANT_ID)}
                    >
                        {params.value}
                    </Typography>
                )
            };
        }
        if (col.field === 'PO_NO') {
            return {
                ...col,
                hide: isHidden,
                cellRenderer: (params) => {
                    const poNo = params.value;
                    const url = getSapPurchaseOrderUrl(poNo);
                    return (
                        <Typography
                            sx={{
                                color: '#DC2626', fontWeight: 500, fontSize: '14px', cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                            }}
                            onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                        >
                            {poNo}
                        </Typography>
                    );
                }
            };
        }
        if (col.field === 'USER_COMMENTS') {
            return {
                ...col,
                hide: isHidden,
                valueGetter: (params) => params.data?.USER_COMMENTS ?? '',
                cellRenderer: (params) => (
                    !isEmptyValue(params.value) ? (
                        <Typography
                            sx={{
                                color: '#DC2626',
                                fontWeight: 400,
                                fontSize: '14px',
                                cursor: 'pointer',
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                            onClick={() => handleViewSupplierComm(params.value, params.data?.PO_NO)}
                        >
                            View Log
                        </Typography>
                    ) : '-'
                )
            };
        }
        if (col.field === 'RECORD_STATUS') {
            return {
                ...col,
                hide: isHidden,
                cellRenderer: (params) => {
                    const status = params.value;
                    if (isEmptyValue(status)) return '-';
                    const { textColor, bgColor } = getSupplierDecisionColor(status);
                    return (
                        <Typography
                            sx={{
                                color: textColor,
                                backgroundColor: bgColor,
                                fontWeight: 500,
                                px: 1,
                                py: 0.25,
                                borderRadius: '10px',
                                display: 'inline-block',
                                fontSize: '12px',
                            }}
                        >
                            {status}
                        </Typography>
                    );
                },
            };
        }
        if (col.field === 'ACTIONS') {
            return {
                ...col,
                hide: isHidden || filterCondition !== 'actionNeeded',
                cellRenderer: (params) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCloseException(params);
                            }}
                            style={{
                                padding: '3px 12px',
                                fontSize: '12px',
                                fontWeight: 500,
                                borderRadius: '6px',
                                border: '1px solid #FECACA',
                                cursor: 'pointer',
                                backgroundColor: '#fff',
                                color: '#DC2626',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FEE2E2'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                        >
                            Close
                        </button>
                    </Box>
                ),
            };
        }
        if (col.field === 'EXCEPTION_STATUS') {
            return {
                ...col,
                hide: isHidden || (filterCondition !== 'actionNeeded' && filterCondition !== 'withExceptions'),
                cellRenderer: (params) => {
                    const recordStatus = (params.data?.RECORD_STATUS || '').toUpperCase();
                    if (recordStatus !== 'NOT SENT') return '-';
                    const val = params.data?.EXCEPTION_STATUS;
                    if (isEmptyValue(val)) return '-';
                    return (
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '13px' }}>
                            {val}
                        </Typography>
                    );
                },
            };
        }
        if (col.field === 'NEW_PO_SHIPPING_INSTRUCTION') {
            return {
                ...col,
                hide: isHidden,
                cellRenderer: (params) => (
                    <div
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <DropdownPicker
                            sx={{ minWidth: '100px' }}
                            value={isEmptyValue(params.data?.NEW_PO_SHIPPING_INSTRUCTION) ? '' : params.data?.NEW_PO_SHIPPING_INSTRUCTION}
                            multiple={false}
                            disabled={isViewerRole}
                            options={[
                                { value: '00 | Road LTL', label: '00 | Road LTL' },
                                { value: '01 | Road FTL', label: '01 | Road FTL' },
                                { value: '02 | Pickup', label: '02 | Pickup' },
                                { value: '03 | Air', label: '03 | Air' },
                                { value: '04 | Service', label: '04 | Service' },
                                { value: '05 | Rail', label: '05 | Rail' },
                                { value: '06 | Sea', label: '06 | Sea' },
                                { value: 'V1 | Shipping instruction 1', label: 'V1 | Shipping instruction 1' },
                                { value: 'V2 | Shipping instruction 2', label: 'V2 | Shipping instruction 2' },
                            ]}
                            handleChange={(e) => handleDropdownChange(params, e.target.value)}
                        />
                    </div>
                )
            };
        }
        if (col.field === 'COLLABORATION_STATUS') {
            return {
                ...col,
                hide: isHidden,
                cellRenderer: (params) => {
                    const recordStatus = (params.data?.RECORD_STATUS || '').toUpperCase();
                    const showButtons = ['APPROVED', 'REJECTED', 'COUNTER OFFER'].includes(recordStatus);
                    const current = isEmptyValue(params.value) || params.value === '-' ? '' : params.value;
                    if (!showButtons) {
                        return <span>{current || '-'}</span>;
                    }
                    if (current) {
                        return (
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    fontSize: '12px',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '10px',
                                    display: 'inline-block',
                                    color: current === 'Accept' ? '#15803d' : '#b45309',
                                    backgroundColor: current === 'Accept' ? '#DCFCE7' : '#FEF3C7',
                                }}
                            >
                                {current}
                            </Typography>
                        );
                    }
                    if (isViewerRole) {
                        return <span>-</span>;
                    }
                    return (
                        <div
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{ display: 'flex', gap: '6px', alignItems: 'center', height: '100%' }}
                        >
                            <button
                                onClick={() => handleCollaborationStatusChange(params, current === 'Accept' ? '' : 'Accept')}
                                style={{
                                    padding: '3px 10px',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    borderRadius: '6px',
                                    border: `1px solid ${current === 'Accept' ? '#15803d' : '#D1D5DB'}`,
                                    cursor: 'pointer',
                                    backgroundColor: current === 'Accept' ? '#DCFCE7' : '#fff',
                                    color: current === 'Accept' ? '#15803d' : '#374151',
                                }}
                            >
                                Accept
                            </button>
                            <button
                                onClick={() => handleCollaborationStatusChange(params, current === 'Defer' ? '' : 'Defer')}
                                style={{
                                    padding: '3px 10px',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    borderRadius: '6px',
                                    border: `1px solid ${current === 'Defer' ? '#b45309' : '#D1D5DB'}`,
                                    cursor: 'pointer',
                                    backgroundColor: current === 'Defer' ? '#FEF3C7' : '#fff',
                                    color: current === 'Defer' ? '#b45309' : '#374151',
                                }}
                            >
                                Defer
                            </button>
                        </div>
                    );
                }
            };
        }
        if (col.field === 'RESCHEDULING_DATE') {
            return {
                ...col,
                hide: isHidden,
                filterParams: DATE_FILTER_PARAMS,
                valueFormatter: (params) => formatDateValue(params.value),
            };
        }
        if (col.field === 'NEW_DELIVERY_DATE') {
            return {
                ...col,
                hide: isHidden,
                width: 220,
                cellRenderer: (params) => (
                    <RescheduleDateCell params={params} field="NEW_DELIVERY_DATE" onCommit={handleDateInputChange('NEW_DELIVERY_DATE')} disabled={isViewerRole} />
                ),
            };
        }
        if (col.field === 'NEW_HANDOVER_DATE') {
            return {
                ...col,
                hide: isHidden,
                width: 220,
                cellRenderer: (params) => (
                    <RescheduleDateCell params={params} field="NEW_HANDOVER_DATE" onCommit={handleDateInputChange('NEW_HANDOVER_DATE')} disabled={isViewerRole} />
                ),
            };
        }
        if (col.field === 'NEW_PO_QUANTITY') {
            return {
                ...col,
                hide: isHidden,
                cellRenderer: (params) => (
                    <QtyEditCell params={params} onCommit={handleTextInputChange} disabled={isViewerRole} />
                )
            };
        }
        return {
            ...col,
            hide: isHidden,
            valueFormatter: col.valueFormatter ?? ((params) => getValueOrDash(params.value)),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [selectedColumns, filterCondition]);

    const filterButtonStyles = {
        textTransform: "none",
        borderRadius: "8px",
        px: 2,
        border: "1px solid #E5E7EB",
        "&.Mui-selected": {
            backgroundColor: "#111827",
            color: "#fff",
            "&:hover": {
                backgroundColor: "#111827",
            },
        },
    };

    const handleSendToSupplier = () => {
        if (selectedRows.length === 0) {
            toast.error('Please select at least one row to send to supplier.', { autoClose: 2000 });
            return;
        }
        const plantIds = [...new Set(selectedRows.map((r) => r.PLANT_ID).filter(Boolean))].join(',');
        const supplierIds = [...new Set(selectedRows.map((r) => r.VENDOR_ID).filter(Boolean))].join(',');
        dispatch(getSupplierEmails({
            dynamicEndpoint: getSupplierEmailsEndpoint,
            params: { plantIds, supplierIds },
        }));
        setSendToSupplierModalOpen(true);
    };

    const handleCreateVariantModal = () => {
        const api = gridRef.current || gridApi;
        setVariantData(api ? api.getFilterModel() : {});
        setOpenCreateVariantModal(true);
    };

    const handleCloseCreateVariantModal = () => {
        setOpenCreateVariantModal(false);
    };

    const handleShowVariantModal = () => {
        setOpenShowVariantModal(true);
    };

    const handleCloseShowVariantModal = () => {
        setOpenShowVariantModal(false);
    };

    const onClickSelectVariant = (variant) => {
        const api = gridRef.current || gridApi;
        if (!api || !variant?.filterJson) return;
        try {
            const filterModel =
                typeof variant.filterJson === 'string'
                    ? JSON.parse(variant.filterJson)
                    : variant.filterJson;
            localStorage.setItem('savedVariantFilterModel_PO_EXCEPTION', JSON.stringify(filterModel));
            api.setFilterModel(filterModel);
            api.onFilterChanged();
        } catch (e) {
            console.error('Invalid filterJson:', variant.filterJson, e);
        }
    };

    const handleFilterConditionChange = (newCondition) => {
        setFilterCondition(newCondition);
        const updatedFormData = { ...formData, exception: [] };
        setFormData(updatedFormData);
        formDataRef.current = updatedFormData;
        fetchTable({ filterCondition: newCondition, filters: updatedFormData });
    };

    const handleClickHide = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseHide = () => {
        setAnchorEl(null);
    };

    const handleColumnChange = (event) => {
        setSelectedColumns(event.target.value || []);
    };

    const renderDropdownValue = (selected) => {
        if (!selected || selected.length === 0) return <em>Select Columns</em>;
        return selected
            .map((field) => columnFilterDropdownOption.find((opt) => opt.value === field)?.label || field)
            .join(', ');
    };

    const handleSave = () => {
        setAnchorEl(null);
        toast.success('Columns updated', { autoClose: 1000 });
    };

    const clearAllFilters = () => {
        if (gridRef.current) {
            gridRef.current.setFilterModel(null);
            setDisableFilterBtn(true);
            try {
                sessionStorage.removeItem(GRID_FILTER_MODEL_STORAGE_KEY);
            } catch (e) {
                // ignore storage errors
            }
        }
    };

    useEffect(() => {
        try {
            const savedFormDataRaw = sessionStorage.getItem(FORM_DATA_STORAGE_KEY);
            if (savedFormDataRaw) {
                const parsed = JSON.parse(savedFormDataRaw);
                if (parsed && typeof parsed === 'object') {
                    setFormData((prev) => ({ ...prev, ...parsed }));
                }
            }

            const savedFilterModelRaw = sessionStorage.getItem(GRID_FILTER_MODEL_STORAGE_KEY);
            if (savedFilterModelRaw) {
                const parsed = JSON.parse(savedFilterModelRaw);
                if (parsed && typeof parsed === 'object') {
                    setSavedGridFilterModel(parsed);
                }
            }

            const savedColumnsRaw = sessionStorage.getItem(HIDDEN_COLUMNS_STORAGE_KEY);
            if (savedColumnsRaw) {
                const parsed = JSON.parse(savedColumnsRaw);
                if (Array.isArray(parsed)) {
                    setSelectedColumns(parsed);
                }
            }
        } catch (e) {
            // ignore restore errors
        }
    }, []);

    useEffect(() => {
        try {
            sessionStorage.setItem(HIDDEN_COLUMNS_STORAGE_KEY, JSON.stringify(selectedColumns || []));
        } catch (e) {
            // ignore storage errors
        }
    }, [selectedColumns]);

    useEffect(() => {
        if (!gridApi || restoredGridFiltersRef.current) return;

        if (savedGridFilterModel && Object.keys(savedGridFilterModel).length > 0) {
            gridApi.setFilterModel(savedGridFilterModel);
            setDisableFilterBtn(false);
        }

        restoredGridFiltersRef.current = true;
    }, [gridApi, savedGridFilterModel]);


    const onGridReady = (params) => {
        setGridApi(params?.api);
        gridRef.current = params?.api;

        // Apply saved variant filter on initial load
        try {
            const saved = localStorage.getItem('savedVariantFilterModel_PO_EXCEPTION');
            if (saved) {
                const parsedFilterModel = JSON.parse(saved);
                params?.api?.setFilterModel(parsedFilterModel);
                params?.api?.onFilterChanged();
            }
        } catch (e) {
            console.error('Error applying saved variant filter:', e);
        }
    }

    const onSelectionChanged = (params) => {
        const rows = params.api.getSelectedRows();
        const uniqueVendorIds = [...new Set(rows.map((r) => r.VENDOR_ID).filter(Boolean))];
        if (uniqueVendorIds.length > 1) {
            toast.error('Please select rows belonging to the same Vendor ID only.', { autoClose: 3000 });
            params.api.deselectAll();
            setSelectedRows([]);
            return;
        }
        setSelectedRows(rows);
    };

    const onFilterChanged = () => {
        if (gridRef.current) {
            const filterModel = gridRef.current.getFilterModel();
            try {
                sessionStorage.setItem(GRID_FILTER_MODEL_STORAGE_KEY, JSON.stringify(filterModel || {}));
            } catch (e) {
                // ignore storage errors
            }
            setDisableFilterBtn(Object.keys(filterModel).length === 0);
        }
    };

    const gridOptions = {
        getRowId: (params) => {
            const d = params.data || {};
            return d.MGMT_ID;
        },
        selectionColumnDef: {
            pinned: 'left',
        },
        onFilterChanged,
        overlayLoadingTemplate: `<span class="ag--overlay-loading-center" >Please wait while data is loading</span>`,
        overlayNoRowsTemplate: `<span class="ag--overlay-no-rows-center" >No Data Available</span>`,
        defaultColDef: {
            sortable: true,
            filter: 'agMultiColumnFilter',
            filterParams: {
                filters: [
                    {
                        filter: 'agTextColumnFilter',
                        filterParams: {
                            suppressMiniFilter: false,
                        },
                    },
                    {
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            excelMode: 'windows',
                            suppressMiniFilter: false,
                            buttons: ['apply', 'reset'],
                        },
                    },
                ],
            },
            cellStyle: {
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.5,
                display: "flex",
                alignItems: "center",
            },
            valueGetter: (params) => {
                if (!params.data) return "-";

                const field = params.colDef.field;
                const value = params.data[field];

                return value === null || value === undefined || value === "" ? "-" : value;
            },
        }

    }

    const columnFilterDropdownOption = useMemo(() => (
        poEmailManagementColumnDefs
            .filter((c) => c.field || c.headerName)
            .map((c) => ({
                value: c.field || c.headerName,
                label: c.headerName || c.field,
            }))
    ), []);

    const rowSelection = {
        mode: "multiRow",
        headerCheckbox: true,
    };

    const fetchDropdownOptions = async (filterdata) => {
        try {
            const transformedFilterData = Object.fromEntries(
                Object.entries(filterdata || {}).map(([key, val]) => {
                    const apiKey = formKeyToApiKey[key] || key;
                    if (Array.isArray(val)) {
                        return [apiKey, val.map((v) => (v && typeof v === 'object' && 'value' in v) ? v.value : v)];
                    }
                    return [apiKey, val];
                })
            );
            const exceptionVals = (filterdata?.exception || []).map((v) => (v && typeof v === 'object' ? v.value : v));
            transformedFilterData.filterCondition = exceptionVals.length > 0 ? exceptionVals[0] : filterCondition;
            const isGlobalRole = userRole === 'Global Planner' || userRole === 'Global Viewer';
            const response = await dispatch(getPOExceptionMgmtBuyerFilter({
                dynamicEndpoint: getPOExceptionMgmtBuyerTablefilterEndpoint,
                params: {
                    role: userRole,
                    ...(isGlobalRole ? {} : { region: selectedRegion }),
                },
                data: transformedFilterData,
            }));
            if (response?.meta?.requestStatus === 'fulfilled') {
                const options = response.payload || [];
                return options.map((opt) => ({ title: String(opt), value: String(opt) }));
            }
            return [];
        } catch (e) {
            console.error(`Failed to fetch dropdown options for ${JSON.stringify(filterdata)}`, e);
            return [];
        }
    };

    const handleExceptionsOpen = (e) => setExceptionsAnchorEl(e.currentTarget);
    const handleExceptionsClose = () => setExceptionsAnchorEl(null);

    const handleSelectedItemsChange = (name, newSelectedItems) => {
        setFormData((prev) => {
            const next = { ...prev, [name]: newSelectedItems };
            formDataRef.current = next;
            return next;
        });
        if (name === 'exception') setIsDirty(true);
    };

    const handleDropdownClose = (name, finalSelectedItems) => {
        const updatedFormData = {
            ...formDataRef.current,
            [name]: finalSelectedItems,
        };
        formDataRef.current = updatedFormData;
        setFormData(updatedFormData);
        const normalizedFormData = Object.fromEntries(
            Object.entries({ ...updatedFormData, role: 'Admin', region: selectedRegion }).map(([key, val]) => {
                if (Array.isArray(val)) {
                    return [key, val.map(v => (v && typeof v === 'object' && 'value' in v) ? v.value : v)];
                }
                return [key, val];
            })
        );
        fetchDropdownOptions(normalizedFormData);
    };

    const filterOptions = useMemo(() => {
        const toOptions = (arr) => (arr || []).map((v) => ({ title: String(v), value: String(v) }));
        return {
            plant: toOptions(availableFilters.plantIds),
            material: toOptions(availableFilters.materialIds),
            mrpController: toOptions(availableFilters.mrpControllerIds),
            supplier: toOptions(availableFilters.supplierIds),
            poNumber: toOptions(availableFilters.poNos),
        };
    }, [availableFilters]);

    const generateFiltersTitle = () => {
        const activeFilters = Object.keys(formData).filter(
            (key) => formData[key] && formData[key].length > 0
        );
        if (isUnconfirmed) activeFilters.push('Unconfirmed');
        if (isAriba) activeFilters.push('Ariba');

        return `Filter Selection Panel ${activeFilters.length > 0
            ? ` (Active Filters: ${activeFilters.join(', ')})`
            : ' (Active Filters: None)'
            }`;
    };

    const handleClearFilters = () => {
        const cleared = getInitialFormData();
        setFormData(cleared);
        formDataRef.current = cleared;
        setFilterCondition('withExceptions');
        setIsUnconfirmed(false);
        setIsAriba(false);
        setIsDirty(false);
        setFiltersExpanded(false);
        fetchTable({ filterCondition: 'withExceptions', filters: cleared, unconfirmed: false, ariba: false });
        fetchDropdownOptions(cleared);
    }

    const applyAndCollapse = () => {
        const exceptionVals = extractValues(formData.exception);
        const activeFilterCondition = exceptionVals.length > 0 ? exceptionVals[0] : filterCondition;
        if (exceptionVals.length > 0) {
            setFilterCondition(exceptionVals[0]);
        }
        setIsDirty(false);
        setFiltersExpanded(false);
        fetchTable({ filterCondition: activeFilterCondition, filters: formData, unconfirmed: isUnconfirmed, ariba: isAriba });
    }

    const hasSelectedFilters = useMemo(() => {
        const hasFormFilters = Object.values(formData).some(
            (value) => Array.isArray(value) && value.length > 0
        );
        return hasFormFilters || isUnconfirmed || isAriba;
    }, [formData, isUnconfirmed, isAriba]);

    return (
        <div>
            <TopSection
                labelList={[
                    { title: "Home", path: "/dashboard" },
                    { title: "PO Exception Management" },
                ]}
                headerName="PO Exception Management"
                hideHeader
            />

            <Box sx={{ flexGrow: 1, p: 1 }}>
                {/* Filter Panel */}
                <Paper sx={{ p: 1, mb: 1 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'pointer',
                            userSelect: 'none',
                            margin: '0px'
                        }}
                        onClick={() => setFiltersExpanded(!filtersExpanded)}
                    >
                        <h3 className="po-filters-title">{generateFiltersTitle()}</h3>
                        <IconButton
                            size="small"
                            sx={{
                                transform: filtersExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                transition: 'transform 0.3s ease',
                                padding: '4px',
                            }}
                        >
                            <ExpandMoreIcon />
                        </IconButton>
                    </Box>
                    <Collapse in={filtersExpanded} timeout="auto" unmountOnExit>
                        <Grid container spacing={1} sx={{ mt: 0 }}>
                            <Grid item xs={2}>
                                <div style={{display: 'flex', flexDirection: 'column', minWidth: '180px', margin: 5}}>
                                    <label className="me-label">Plant</label>
                                    <MultiDropdownSelection
                                        id="plant"
                                        name="plant"
                                        options={getOptions('plantIds')}
                                        selectedItemsData={formData.plant || []}
                                        onChangeItems={handleSelectedItemsChange}
                                        onDropdownClose={handleDropdownClose}
                                        height={37}
                                        disabled={dropdownLoading}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={2}>
                                <div style={{ margin: 5 }}>
                                    <label className="me-label">Material</label>
                                    <MultiDropdownSelection
                                        id="material"
                                        name="material"
                                        options={getOptions('materialIds')}
                                        selectedItemsData={formData.material || []}
                                        onChangeItems={handleSelectedItemsChange}
                                        onDropdownClose={handleDropdownClose}
                                        height={37}
                                        disabled={dropdownLoading}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={2}>
                                <div style={{ margin: 5 }}>
                                    <label className="me-label">MRP Controller</label>
                                    <MultiDropdownSelection
                                        id="mrpController"
                                        name="mrpController"
                                        options={getOptions('mrpControllers')}
                                        selectedItemsData={formData.mrpController || []}
                                        onChangeItems={handleSelectedItemsChange}
                                        onDropdownClose={handleDropdownClose}
                                        height={37}
                                        disabled={dropdownLoading}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={2}>
                                <div style={{ margin: 5 }}>
                                    <label className="me-label">Vendor</label>
                                    <MultiDropdownSelection
                                        id="supplier"
                                        name="supplier"
                                        options={getOptions('vendorIds')}
                                        selectedItemsData={formData.supplier || []}
                                        onChangeItems={handleSelectedItemsChange}
                                        onDropdownClose={handleDropdownClose}
                                        height={37}
                                        disabled={dropdownLoading}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={2}>
                                <div style={{ margin: 5 }}>
                                    <label className="me-label">PO #</label>
                                    <MultiDropdownSelection
                                        id="poNumber"
                                        name="poNumber"
                                        options={getOptions('poNos')}
                                        selectedItemsData={formData.poNumber || []}
                                        onChangeItems={handleSelectedItemsChange}
                                        onDropdownClose={handleDropdownClose}
                                        height={37}
                                        disabled={dropdownLoading}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={2} />
                            <Grid item xs={2}>
                                <div style={{ margin: 5 }}>
                                    <Box>
                                        <CustomButton
                                            label={`Exceptions${formData.exception?.length ? ` (${formData.exception.length})` : ''}`}
                                            endIcon={<ChevronRightIcon fontSize="small" />}
                                            type="secondary"
                                            height="35px"
                                            onClick={handleExceptionsOpen}
                                        />
                                        <Popover
                                            open={Boolean(exceptionsAnchorEl)}
                                            anchorEl={exceptionsAnchorEl}
                                            onClose={handleExceptionsClose}
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                            PaperProps={{
                                                sx: {
                                                    borderRadius: '8px',
                                                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                                    mt: 0.5,
                                                }
                                            }}
                                        >
                                            <Box sx={{ width: 270, py: 1 }}>
                                                <Typography
                                                    variant="subtitle2"
                                                    fontWeight={600}
                                                    sx={{ px: 2, py: 1, borderBottom: '1px solid #E5E7EB', color: '#374151' }}
                                                >
                                                    Filter by Exception
                                                </Typography>
                                                {EXCEPTION_OPTIONS.map((opt) => {
                                                    const isSelected = (formData.exception || []).some(
                                                        (e) => (typeof e === 'object' ? e.value : e) === opt.value
                                                    );
                                                    return (
                                                        <Box
                                                            key={opt.value}
                                                            onClick={() => {
                                                                const next = isSelected
                                                                    ? []
                                                                    : [{ title: opt.label, value: opt.value }];
                                                                handleSelectedItemsChange('exception', next);
                                                                if (!isSelected) {
                                                                    setFilterCondition(opt.value);
                                                                } else {
                                                                    setFilterCondition('withExceptions');
                                                                }
                                                            }}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1.5,
                                                                px: 2,
                                                                py: 0.85,
                                                                cursor: 'pointer',
                                                                backgroundColor: isSelected ? '#F0F9FF' : 'transparent',
                                                                '&:hover': { backgroundColor: isSelected ? '#E0F2FE' : '#F9FAFB' },
                                                            }}
                                                        >
                                                            <Checkbox
                                                                checked={isSelected}
                                                                size="small"
                                                                sx={{
                                                                    p: 0,
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ flexGrow: 1, color: opt.color, fontWeight: isSelected ? 600 : 400 }}
                                                            >
                                                                {opt.label}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                                {formData.exception?.length > 0 && (
                                                    <Box sx={{ px: 2, pt: 1, borderTop: '1px solid #E5E7EB', mt: 0.5 }}>
                                                        <Typography
                                                            variant="body2"
                                                            onClick={(e) => { e.stopPropagation(); handleSelectedItemsChange('exception', []); setFilterCondition('withExceptions'); }}
                                                            sx={{ color: '#2563EB', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                                        >
                                                            Clear selection
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Popover>
                                    </Box>
                                </div>
                            </Grid>
                            <Grid item xs={2}>
                                <Box
                                    onClick={() => { setIsUnconfirmed((prev) => !prev); setIsDirty(true); }}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, margin: '5px', height: '35px', cursor: 'pointer', userSelect: 'none' }}
                                >
                                    <Checkbox checked={isUnconfirmed} size="small" sx={{ p: 0 }} />
                                    <Typography variant="body2" sx={{ fontWeight: isUnconfirmed ? 600 : 400, fontSize: '13px' }}>
                                        Unconfirmed
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={2}>
                                <Box
                                    onClick={() => { setIsAriba((prev) => !prev); setIsDirty(true); }}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, margin: '5px', height: '35px', cursor: 'pointer', userSelect: 'none' }}
                                >
                                    <Checkbox checked={isAriba} size="small" sx={{ p: 0 }} />
                                    <Typography variant="body2" sx={{ fontWeight: isAriba ? 600 : 400, fontSize: '13px' }}>
                                        Ariba
                                    </Typography>
                                </Box>
                            </Grid>

                        </Grid>
                        <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mx: 1, mb: 2 }}>
                            <CustomButton
                                label="Clear Filters"
                                type="primary"
                                height="35px"
                                onClick={handleClearFilters}
                            />
                            <CustomButton
                                label="Apply and Collapse"
                                type="secondary"
                                height="35px"
                                disabled={!hasSelectedFilters && !isDirty}
                                onClick={applyAndCollapse}
                            />
                        </Box>
                    </Collapse>
                </Paper>
                {/* Summary Cards */}
                {/* <div className="container">
                <Cards cards={cardData} showBorder={true} callBackFunction={callBackFunction} />
            </div> */}
                <Box sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    margin: '0px',
                    backgroundColor: '#f5f5f5',
                    height: 'auto',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                    padding: '12px',
                    borderRadius: '8px',
                }}>
                    {/* Quick Filters */}
                    <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1, mx: 1 }}>
                        <Typography fontWeight={600}>Quick Filters:</Typography>
                        <ToggleButtonGroup
                            value={filterCondition}
                            exclusive
                            onChange={(e, val) => { if (val !== null) handleFilterConditionChange(val); }}
                        >
                            <ToggleButton value="all" sx={filterButtonStyles}>All</ToggleButton>
                            <ToggleButton value="withExceptions" sx={filterButtonStyles}>Exceptions Management</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    <Box display="flex" flexDirection="row" justifyContent="space-between" gap={2} sx={{ mt: 2, mb: 1 }}>
                        {/* Filter Buttons */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton onClick={handleClickHide}>
                                    <Badge badgeContent={selectedColumns.length} sx={{
                                        "& .MuiBadge-badge": {
                                            fontSize: "0.6rem", // Smaller font size
                                            height: "16px", // Adjust badge height
                                            minWidth: "16px", // Adjust badge width
                                            padding: "4px", // Adjust padding
                                            background: '#000',
                                            color: '#fff'
                                        },
                                    }}>
                                        <MoreVertIcon />
                                    </Badge>
                                </IconButton>

                                <Popover
                                    open={Boolean(anchorEl)}
                                    anchorEl={anchorEl}
                                    onClose={handleCloseHide}
                                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                                    className="hidePopover"
                                    sx={{
                                        marginTop: 1,
                                        marginLeft: 0,
                                    }}
                                >
                                    <Box sx={{ padding: '10px', width: 150 }}>
                                        <p style={{ marginBottom: 10, marginTop: 5 }}>Hide Columns</p>
                                        <Select
                                            multiple
                                            value={selectedColumns}
                                            onChange={handleColumnChange}
                                            renderValue={renderDropdownValue}
                                            displayEmpty
                                            sx={{ backgroundColor: "#fff", border: '1px solid #000', width: '100%', height: 35, marginBottom: 5 }}
                                        >
                                            <MenuItem disabled value="">
                                                <em>Select Columns</em>
                                            </MenuItem>
                                            {columnFilterDropdownOption?.map(col => (
                                                <MenuItem key={col.value} value={col.value}>
                                                    <Checkbox checked={selectedColumns?.includes(col.value)} />
                                                    <ListItemText primary={col.label} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <CustomButton type="secondary" label="Save" onClick={handleSave} />
                                    </Box>
                                </Popover>
                            </Box>

                            <CustomButton
                                maxWidth="150px"
                                label="Clear All Filters"
                                bgColor="black"
                                color="white"
                                disabled={disableFilterBtn}
                                onClick={clearAllFilters}
                            />
                            <CustomButton
                                maxWidth="180px"
                                label="Create/Edit Variant"
                                bgColor="black"
                                color="white"
                                onClick={handleCreateVariantModal}
                            />
                            <CustomButton
                                maxWidth="150px"
                                label="Show Variants"
                                bgColor="black"
                                color="white"
                                onClick={handleShowVariantModal}
                                sx={{ ml: 1 }}
                            />
                        </Box>
                        {/* Action Buttons */}
                        <Box display="flex" alignSelf="flex-end" gap={2}>
                            <CustomButton
                                label="Send to Supplier"
                                type="secondary"
                                maxWidth="200px"
                                // disabled={!(filterCondition === 'withExceptions' || filterCondition === 'actionNeeded') || isViewerRole}
                                disabled={true}
                                onClick={handleSendToSupplier}
                            />
                        </Box>
                    </Box>
                    <div style={{ margin: "10px 0px" }}>
                        <div className="ag-theme-quartz" style={{ height: 400, width: "100%" }}>
                            {poExceptionMgmtBuyerTableSelector.loading ?
                                <Loader />
                                :
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={trackingData || []}
                                    columnDefs={columnDefsWithHandlers}
                                    onGridReady={onGridReady}
                                    onSelectionChanged={onSelectionChanged}
                                    gridOptions={gridOptions}
                                    rowSelection={rowSelection}
                                    pagination={true}
                                    paginationPageSize={1000}
                                    paginationPageSizeSelector={[10, 50, 100, 500, 1000, 2000]}
                                />}
                        </div>

                    </div>

                </Box>
                <VariantViewModal
                    tableType="PO_EXCEPTION"
                    appCode="SC"
                    plant={selectedPlant || "ALL"}
                    region={['Global Planner', 'Global Viewer'].includes(userRole) ? "ALL" : (selectedRegion || "ALL")}
                    openCreateVariantModal={openCreateVariantModal}
                    handleCloseCreateVariantModal={handleCloseCreateVariantModal}
                    data={variantData}
                />
                <ShowVariantViewModal
                    tableType="PO_EXCEPTION"
                    appCode="SC"
                    plant={selectedPlant || "ALL"}
                    region={['Global Planner', 'Global Viewer'].includes(userRole) ? "ALL" : (selectedRegion || "ALL")}
                    openShowVariantModal={openShowVariantModal}
                    handleCloseShowVariantModal={handleCloseShowVariantModal}
                    onClickSelectVariant={onClickSelectVariant}
                />
                <BatchTable
                    isShown={batchModalOpen}
                    selectedMaterial={selectedMaterialBatches?.material}
                    materialBatchesData={materialBatchDataSelector?.data ?? []}
                    loading={materialBatchDataSelector?.loading}
                    handleBatchDialogClose={handleBatchDialogClose}
                />
                <Dialog open={sendToSupplierModalOpen} onClose={handleCloseSendModal} fullWidth maxWidth="sm">
                    <DialogTitle sx={{ pb: 0.5 }}>
                        <Typography variant="h6" fontWeight={600}>Send Notification to Supplier</Typography>
                        <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
                            {selectedRows.length} PO(s) selected • Select recipients to notify about changes
                        </Typography>
                    </DialogTitle>
                    <DialogContent dividers>
                        {/* Selected Purchase Orders */}
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Selected Purchase Orders</Typography>
                        <Box sx={{ p: 1.5, backgroundColor: '#F9FAFB', borderRadius: '6px', border: '1px solid #E5E7EB', mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {selectedRows.map((row, idx) => (
                                <Box key={idx} sx={{ borderBottom: idx < selectedRows.length - 1 ? '1px solid #E5E7EB' : 'none', pb: idx < selectedRows.length - 1 ? 1 : 0 }}>
                                    <Typography variant="body2" fontWeight={500}>{row.PO_NO}/{row.PO_ITEM_NO}</Typography>
                                    <Typography variant="body2" sx={{ color: '#6B7280' }}>{row.MATERIAL_NAME}</Typography>
                                </Box>
                            ))}
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        {/* Recipients */}
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>Select Recipients</Typography>
                        {supplierEmailsSelector?.loading ? (
                            <Typography variant="body2" sx={{ color: '#6B7280', py: 1 }}>Loading recipients...</Typography>
                        ) : recipients.length === 0 ? (
                            <Typography variant="body2" sx={{ color: '#6B7280', py: 1 }}>No recipients found.</Typography>
                        ) : null}
                        {!supplierEmailsSelector?.loading && recipients.map((recipient) => (
                            <Box
                                key={recipient.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1.5,
                                    py: 1.5,
                                    px: 1,
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: '#F9FAFB' },
                                }}
                                onClick={() => handleRecipientToggle(recipient.id)}
                            >
                                <Checkbox
                                    checked={selectedRecipients.includes(recipient.id)}
                                    onChange={() => handleRecipientToggle(recipient.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ p: 0, mt: 0.25 }}
                                />
                                <Box sx={{ flexGrow: 1 }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="body2" fontWeight={500}>{recipient.email}</Typography>
                                        <Chip
                                            label={recipient.role}
                                            size="small"
                                            sx={{
                                                height: 18,
                                                fontSize: '10px',
                                                backgroundColor: recipient.role === 'parent' ? '#DBEAFE' : '#F3E8FF',
                                                color: recipient.role === 'parent' ? '#1D40AE' : '#6B21A8',
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body2" sx={{ color: '#6B7280' }}>{recipient.name}</Typography>
                                    <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                                        {recipient.vendorCode} • {recipient.company}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                        <Box sx={{ mt: 1.5, pl: 1 }}>
                            <Typography
                                variant="body2"
                                onClick={handleAddEmailToggle}
                                sx={{ color: '#2563EB', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            >
                                + Add new email address
                            </Typography>
                            <Collapse in={addEmailFormOpen} timeout="auto" unmountOnExit>
                                <Box
                                    sx={{
                                        mt: 1.5,
                                        p: 2,
                                        backgroundColor: '#F9FAFB',
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '8px',
                                    }}
                                >
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                                        Add Email Address
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Contact Name"
                                            value={newEmailData.contactName}
                                            onChange={(e) => setNewEmailData((prev) => ({ ...prev, contactName: e.target.value }))}
                                        />
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Email Address"
                                            value={newEmailData.emailAddress}
                                            onChange={(e) => setNewEmailData((prev) => ({ ...prev, emailAddress: e.target.value }))}
                                        />

                                    </Box>
                                    <Box display="flex" gap={1} sx={{ mt: 2 }}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={handleAddEmailSave}
                                            disabled={!newEmailData.contactName || !newEmailData.emailAddress}
                                            sx={{
                                                backgroundColor: '#000000',
                                                '&:hover': { backgroundColor: '#000000' },
                                                '&.Mui-disabled': { backgroundColor: '#dbdbdc', color: '#fff' },
                                                textTransform: 'none',
                                                color: '#fff',
                                                fontWeight: 500,
                                            }}
                                        >
                                            Save
                                        </Button>
                                        <Button
                                            variant="text"
                                            size="small"
                                            onClick={handleAddEmailCancel}
                                            sx={{
                                                textTransform: 'none',
                                                color: '#374151',
                                                '&:hover': { backgroundColor: '#F3F4F6' },
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </Box>
                                </Box>
                            </Collapse>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={handleCloseSendModal}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#D1D5DB',
                                color: '#374151',
                                '&:hover': { borderColor: '#9CA3AF', backgroundColor: '#F9FAFB' },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disabled={selectedRecipients.length === 0}
                            onClick={handleSendNotification}
                            sx={{
                                backgroundColor: '#000',
                                '&:hover': { backgroundColor: '#333' },
                                '&.Mui-disabled': { backgroundColor: '#E5E7EB', color: '#9CA3AF' },
                                textTransform: 'none',
                                color: '#fff',
                            }}
                        >
                            Send Notification ({selectedRecipients.length})
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={supplierCommModalOpen} onClose={() => setSupplierCommModalOpen(false)} fullWidth maxWidth="sm">
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Communication Log - {supplierCommData?.po}</Typography>
                        <Box sx={{
                            maxHeight: 400,
                            overflowY: 'auto',
                            padding: 2,
                            backgroundColor: '#f9f9f9',
                            borderRadius: '8px',
                            border: '1px solid #ddd',
                        }}>
                            <Typography sx={{ whiteSpace: 'pre-line' }}>{supplierCommData?.comm}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="flex-end">
                            <Button
                                variant="contained"
                                onClick={() => setSupplierCommModalOpen(false)}
                                sx={{
                                    backgroundColor: "#000",
                                    "&:hover": { backgroundColor: "#000" },
                                    textTransform: "none",
                                    color: "#fff",
                                    mt: 2,
                                }}
                            >
                                Close
                            </Button>
                        </Box>
                    </Box>
                </Dialog>
            </Box>
        </div>
    );
};

export default POExceptionMgmtBuyer;